/**
 * Which market a «Scegli un mercato» chooser opens on when its URL names none — owner ruling
 * 2026-10-09 (demo request 5), `docs/adr/9056`, the third exception to CLAUDE.md §3.4 rule 1
 * after the run default (ADR 6011/9039) and Simulate's sandbox (ADR 6013).
 *
 * **The rule, in one place** ({@link onlyVisibleMarket}, pure):
 *
 *   1. a market the URL names — always wins, nothing is overridden;
 *   2. when the signed-in person can see EXACTLY ONE market — the reads the chooser already lists
 *      its options from all answered, and between them they name one market and no other — that
 *      market. There is nothing to choose: the only answer the chooser could take is that one;
 *   3. otherwise none — the chooser asks, as before. Several visible markets are never narrowed to
 *      one for the planner, not "the first", not "the one with the newest run".
 *
 * Step 2 is written INTO the URL with `history.replaceState` by the caller's `write`, so every
 * read is still bounded by a market the address bar names (§3.4, G16), the link is shareable, and
 * Back leaves the page rather than returning to the address with no market.
 *
 * **While a read is loading, or has failed, nothing is chosen.** A list built from a failed read
 * is not "the markets this person can see", and one market among a partial answer would be a
 * guess. The chooser waits, then asks.
 *
 * **What the page says.** A market chosen for the planner is never silently in force: while — and
 * only while — the URL still names the market this module chose, {@link MarketDefaultNote} says
 * so beside the chooser («L'unico mercato che puoi vedere: scelto per te»).
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The market to open on for the planner, or `null`. Pure.
 *
 * `visible` is every market the person's reads name, `undefined` while any of those reads is
 * loading or has failed.
 */
export function onlyVisibleMarket(linked: string | null, visible: readonly string[] | undefined): string | null {
  if (linked !== null || visible === undefined) return null;
  const distinct = [...new Set(visible.map((market) => market.trim()).filter((market) => market !== ''))];
  return distinct.length === 1 ? (distinct[0] ?? null) : null;
}

/**
 * The markets chosen for the planner in this tab, by chooser key. Module state on purpose, as the
 * run default's: the gate that chose and the caption that says so may be different components.
 */
const autoMarkets = new Map<string, string>();

/** Test seam: forget every market chosen in this tab. */
export function clearAutoMarkets(): void {
  autoMarkets.clear();
}

/** Whether the URL's market on `key` is the one this module chose for the planner. */
export function marketChosenFor(key: string, linked: string | null): boolean {
  return linked !== null && autoMarkets.get(key) === linked;
}

/**
 * Write one key into the address bar's query, replacing the history entry, and tell the page's
 * readers (they listen for `popstate`, as for every in-app navigation).
 */
export function replaceSearchParam(name: string, value: string): void {
  if (typeof window === 'undefined') return;
  const next = new URLSearchParams(window.location.search);
  next.set(name, value);
  window.history.replaceState(window.history.state, '', `${window.location.pathname}?${next.toString()}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export interface UseMarketDefaultOptions {
  /** Which chooser this is — `foundations:country`, `postseason:promotions` — for the caption. */
  readonly key: string;
  /** The market the URL names, or `null`. */
  readonly linked: string | null;
  /** Every market the person's reads name; `undefined` while any of them is loading or failed. */
  readonly visible: readonly string[] | undefined;
  /** Write the market chosen for the planner into the URL, replacing the history entry. */
  readonly write: (market: string) => void;
  /** `false` turns the default off — a chooser not on screen in this state. */
  readonly enabled?: boolean;
}

/**
 * Open the chooser on the only visible market, once, after the render that found it. Returns
 * whether the market now in the URL was chosen this way — what {@link MarketDefaultNote} says.
 */
export function useMarketDefault({ key, linked, visible, write, enabled = true }: UseMarketDefaultOptions): boolean {
  const target = enabled ? onlyVisibleMarket(linked, visible) : null;
  useEffect(() => {
    if (target === null) return;
    autoMarkets.set(key, target);
    write(target);
    // `write` is the caller's and may be a fresh closure each render; the market decides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, target]);
  return marketChosenFor(key, linked);
}

/** The caption beside a chooser whose market was chosen for the planner; nothing otherwise. */
export function MarketDefaultNote({ chosen, market }: {
  readonly chosen: boolean;
  readonly market: string | null;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!chosen || market === null) return null;
  return (
    <span className="text-caption text-ink-secondary" data-market-default={market}>
      {t('marketDefault.onlyVisible', { market })}
    </span>
  );
}
